import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Stack,
  Title,
  Card,
  Button,
  Group,
  Alert,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Skeleton,
  SimpleGrid,
  Modal,
  Paper,
  Box,
  Grid,
  NumberInput,
  Flex,
  Select,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconPhoto,
  IconX,
  IconRefresh,
  IconSeedling,
  IconClock,
  IconFlame,
  IconUsers,
  IconToolsKitchen2,
  IconChevronLeft,
  IconChevronRight,
  IconSlice,
  IconChefHat,
  IconChartBar,
  IconStarFilled,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useAppUser } from '../hooks/useAppUser';
import { usePageTitle } from '../hooks/usePageTitle';
import { RecipeImage } from '../components/UI/RecipeImage';
import { MoreRestrictionsMenu } from '../components/UI/MoreRestrictionsMenu';
import { RecipeBadge, splitBadgesForDisplay } from '../utils/recipeBadges';
import { formatTime } from '../utils/formatTime';
import { formatQuantity } from '../utils/formatQuantity';
import { NutritionDrawer } from '../components/Recipe/NutritionDrawer';
import {
  SeedRecipe,
  fetchSeedQueue,
  approveSeedRecipe,
  deleteSeedRecipe,
  editSeedRecipe,
  deleteAllSeedRecipes,
  generateSeedRecipeImage,
  triggerCatalogSeeding,
} from '../services/adminCatalogService';
import { CuisineBadge } from '../components/UI/CuisineBadge';
import {
  CategorizationMenu,
  RecipeSlotsValue,
  categoryLabel,
  deriveSlots,
} from '../components/Admin/CategorizationMenu';

// Color the tails: MyPlate's rating distribution is compressed around ~3.6,
// so only the extremes carry review signal. Applies to any future source's
// ratings; recipes without a sourceRating (AI-seeded) render nothing.
const ratingColor = (value: number): string | undefined =>
  value >= 4 ? 'teal' : value < 3 ? 'red' : undefined;

const SourceRating = ({
  rating,
  iconSize = 14,
}: {
  rating?: { value: number; count: number };
  iconSize?: number;
}) => {
  if (!rating || !(rating.count > 0)) return null;
  return (
    <Tooltip withArrow label={`Rated ${rating.value.toFixed(2)} by ${rating.count} people on the source site`} styles={{ tooltip: { fontSize: '0.75rem', lineHeight: 1.4 } }}>
      <Group gap={4} wrap="nowrap">
        <IconStarFilled
          size={iconSize}
          style={{ color: 'var(--mantine-color-yellow-6)' }}
        />
        <Text size="sm" fw={500} c={ratingColor(rating.value)}>
          {rating.value.toFixed(1)}
        </Text>
        <Text size="xs" c="dimmed">
          ({rating.count})
        </Text>
      </Group>
    </Tooltip>
  );
};

const difficultyColor = (d?: string) =>
  d === 'easy' ? 'green' : d === 'medium' ? 'yellow' : 'red';

type SeedSlots = RecipeSlotsValue;

const SeedRecipeCard = ({
  recipe,
  onOpenDetails,
  onApprove,
  onDelete,
  onGenerateImage,
  onRecategorize,
  onEditSlots,
}: {
  recipe: SeedRecipe;
  onOpenDetails: (id: string) => void;
  onApprove: (id: string) => void;
  onDelete: (id: string) => void;
  onGenerateImage: (id: string) => void;
  onRecategorize: (id: string, category: string) => void;
  onEditSlots: (id: string, slots: SeedSlots) => void;
}) => {
  const [imageLoading, setImageLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const handleGenerateImage = async () => {
    setImageLoading(true);
    try {
      await onGenerateImage(recipe.id);
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <Card padding="lg" radius="lg" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
      <Card.Section style={{ position: 'relative', cursor: 'pointer' }} onClick={() => onOpenDetails(recipe.id)}>
        <RecipeImage
          src={recipe.imageUrl}
          alt={recipe.name}
          height={200}
        />
        {recipe.cuisine && (
          <CuisineBadge
            cuisine={recipe.cuisine}
            placement="overlay"
            style={{ position: 'absolute', top: 8, left: 8 }}
          />
        )}
        {/* Curation control: category (taxonomy) + serving slots. Badge is
            the target; propagation stopped so the card click doesn't open
            details. */}
        <CategorizationMenu
          category={recipe.category}
          slots={recipe.slots}
          onChangeCategory={(category) => onRecategorize(recipe.id, category)}
          onChangeSlots={(slots) => onEditSlots(recipe.id, slots)}
          target={
            <Badge
              size="sm"
              variant="filled"
              color={
                recipe.category === 'breakfast' || recipe.category === 'lunch'
                  ? 'gray'
                  : 'dark'
              }
              style={{ position: 'absolute', top: 8, right: 8, cursor: 'pointer' }}
              onClick={(e) => e.stopPropagation()}
            >
              {categoryLabel(recipe.category)}
            </Badge>
          }
        />
      </Card.Section>

      <Stack gap="xs" mt="md" style={{ flex: 1 }}>
        <Text fw={500} lineClamp={2} style={{ cursor: 'pointer' }} onClick={() => onOpenDetails(recipe.id)}>
          {recipe.name}
        </Text>
        {recipe.description && (
          <Text size="sm" c="dimmed" lineClamp={2}>
            {recipe.description}
          </Text>
        )}

        <Group gap="md">
          <Group gap="xs">
            <IconClock size={16} />
            <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
          </Group>
          <Group gap="xs">
            <IconUsers size={16} />
            <Text size="sm">{recipe.servings}</Text>
          </Group>
          {recipe.difficulty && (
            <Badge size="sm" variant="light" color={difficultyColor(recipe.difficulty)}>
              {recipe.difficulty}
            </Badge>
          )}
          <SourceRating rating={recipe.sourceRating} />
        </Group>

        <Group gap="xs">
          {recipe.mealPrepFriendly && (
            <Badge size="sm" variant="outline">meal prep</Badge>
          )}
          {(() => {
            const dietaryRestrictions = recipe.dietaryRestrictions || [];
            const tags = recipe.tags || [];
            const allBadges: RecipeBadge[] = [
              ...dietaryRestrictions.map((r) => ({ text: r, type: 'dietary' as const })),
              ...tags.map((t) => ({ text: t, type: 'tag' as const })),
            ];
            const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, 3);
            return (
              <>
                {visible.map((badge) => (
                  <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline">
                    {badge.text}
                  </Badge>
                ))}
                <MoreRestrictionsMenu restrictions={hiddenRestrictions} />
              </>
            );
          })()}
        </Group>

        <Group justify="flex-end" mt="auto" pt="xs">
          <Group gap="xs">
            <Tooltip label="Regenerate image">
              <ActionIcon
                variant="light"
                color="blue"
                loading={imageLoading}
                onClick={handleGenerateImage}
              >
                <IconPhoto size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Approve">
              <ActionIcon
                variant="light"
                color="green"
                loading={actionLoading === 'approve'}
                onClick={async () => {
                  setActionLoading('approve');
                  try {
                    await onApprove(recipe.id);
                  } finally {
                    setActionLoading(null);
                  }
                }}
              >
                <IconCheck size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Reject">
              <ActionIcon
                variant="light"
                color="red"
                loading={actionLoading === 'delete'}
                onClick={async () => {
                  setActionLoading('delete');
                  try {
                    await onDelete(recipe.id);
                  } finally {
                    setActionLoading(null);
                  }
                }}
              >
                <IconX size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Stack>
    </Card>
  );
};

/**
 * Full-details review modal, built for clearing a large queue fast: verdict
 * buttons live in the modal, a verdict auto-advances to the next recipe, and
 * prev/next (or arrow keys) move through the queue without closing. A/R are
 * approve/reject shortcuts.
 */
const SeedRecipeReviewModal = ({
  recipe,
  index,
  total,
  hasMore,
  actionLoading,
  onClose,
  onPrev,
  onNext,
  onApprove,
  onReject,
  onGenerateImage,
  onRecategorize,
  onEditSlots,
}: {
  recipe: SeedRecipe;
  index: number;
  total: number;
  hasMore: boolean;
  actionLoading: 'approve' | 'reject' | null;
  onClose: () => void;
  onPrev: (() => void) | null;
  onNext: (() => void) | null;
  onApprove: () => void;
  onReject: () => void;
  onGenerateImage: (id: string) => Promise<void>;
  onRecategorize: (id: string, category: string) => void;
  onEditSlots: (id: string, slots: SeedSlots) => void;
}) => {
  const [nutritionOpen, setNutritionOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (nutritionOpen || actionLoading) return;
      if (e.key === 'ArrowRight' && onNext) onNext();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'a' || e.key === 'A') onApprove();
      else if (e.key === 'r' || e.key === 'R') onReject();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nutritionOpen, actionLoading, onNext, onPrev, onApprove, onReject]);

  const handleGenerateImage = async () => {
    setImageLoading(true);
    try {
      await onGenerateImage(recipe.id);
    } finally {
      setImageLoading(false);
    }
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={recipe.name}
      size="90%"
      styles={{ content: { maxWidth: 1200 } }}
      trapFocus={false}
    >
      <Stack gap="lg">
        <Group justify="space-between" wrap="wrap">
          <Group gap="xs">
            <ActionIcon
              variant="default"
              size="lg"
              disabled={!onPrev}
              onClick={() => onPrev?.()}
              title="Previous (left arrow)"
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text size="sm" c="dimmed" style={{ minWidth: 90, textAlign: 'center' }}>
              {index + 1} of {total}{hasMore ? '+' : ''}
            </Text>
            <ActionIcon
              variant="default"
              size="lg"
              disabled={!onNext}
              onClick={() => onNext?.()}
              title="Next (right arrow)"
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
          <Group gap="xs">
            {/* Same curation control the grid badge offers: the verdict
                usually forms here, mid-review */}
            <CategorizationMenu
              category={recipe.category}
              slots={recipe.slots}
              onChangeCategory={(category) => onRecategorize(recipe.id, category)}
              onChangeSlots={(slots) => onEditSlots(recipe.id, slots)}
              target={
                <Button variant="default" size="sm">
                  {categoryLabel(recipe.category)}
                </Button>
              }
            />
            <Tooltip withArrow label="Regenerate image">
              <ActionIcon
                variant="light"
                color="blue"
                size="lg"
                loading={imageLoading}
                onClick={handleGenerateImage}
              >
                <IconPhoto size={18} />
              </ActionIcon>
            </Tooltip>
            <Button
              variant="light"
              color="red"
              leftSection={<IconX size={16} />}
              loading={actionLoading === 'reject'}
              disabled={actionLoading === 'approve'}
              onClick={onReject}
            >
              Reject (R)
            </Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              loading={actionLoading === 'approve'}
              disabled={actionLoading === 'reject'}
              onClick={onApprove}
            >
              Approve (A)
            </Button>
          </Group>
        </Group>
          <Flex gap="lg" direction={{ base: 'column', sm: 'row' }} align="flex-start">
            <Box
              style={{ borderRadius: 8, overflow: 'hidden', flexShrink: 0, position: 'relative' }}
              w={{ base: '100%', sm: 300, lg: 400 }}
              h={{ base: 280, sm: 200, lg: 250 }}
            >
              <RecipeImage
                src={recipe.imageUrl}
                alt={recipe.name}
                width="100%"
                height="100%"
              />
              {recipe.cuisine && (
                <CuisineBadge
                  cuisine={recipe.cuisine}
                  placement="overlay"
                  style={{ position: 'absolute', top: 8, left: 8 }}
                />
              )}
            </Box>

            <Stack gap="md" style={{ flex: 1 }}>
              {recipe.description && (
                <Text size="sm" c="dimmed">{recipe.description}</Text>
              )}

              <Group gap="lg" wrap="wrap">
                <Group gap="xs" align="flex-start">
                  <IconSlice size={20} style={{ marginTop: 2 }} />
                  <Box>
                    <Text size="sm" fw={500}>Prep time</Text>
                    <Text size="sm" c="dimmed">{formatTime(recipe.prepTimeMinutes)}</Text>
                  </Box>
                </Group>
                <Group gap="xs" align="flex-start">
                  <IconFlame size={20} style={{ marginTop: 2 }} />
                  <Box>
                    <Text size="sm" fw={500}>Cook time</Text>
                    <Text size="sm" c="dimmed">{formatTime(recipe.cookTimeMinutes)}</Text>
                  </Box>
                </Group>
                <Group gap="xs" align="flex-start">
                  <IconClock size={20} style={{ marginTop: 2 }} />
                  <Box>
                    <Text size="sm" fw={500}>Total time</Text>
                    <Text size="sm" c="dimmed">{formatTime(recipe.totalTimeMinutes)}</Text>
                  </Box>
                </Group>
                <Group gap="xs" align="flex-start">
                  <IconUsers size={20} style={{ marginTop: 2 }} />
                  <Box>
                    <Text size="sm" fw={500}>Servings</Text>
                    <Text size="sm" c="dimmed">{recipe.servings}</Text>
                  </Box>
                </Group>
                {recipe.difficulty && (
                  <Group gap="xs" align="flex-start">
                    <IconChefHat size={20} style={{ marginTop: 2 }} />
                    <Box>
                      <Text size="sm" fw={500}>Difficulty</Text>
                      <Badge size="sm" variant="light" color={difficultyColor(recipe.difficulty)}>
                        {recipe.difficulty}
                      </Badge>
                    </Box>
                  </Group>
                )}
                {recipe.sourceRating && recipe.sourceRating.count > 0 && (
                  <Group gap="xs" align="flex-start">
                    <IconStarFilled
                      size={20}
                      style={{ marginTop: 2, color: 'var(--mantine-color-yellow-6)' }}
                    />
                    <Box>
                      <Text size="sm" fw={500}>Source rating</Text>
                      <Text size="sm" c={ratingColor(recipe.sourceRating.value) ?? 'dimmed'}>
                        {recipe.sourceRating.value.toFixed(1)} ({recipe.sourceRating.count} ratings)
                      </Text>
                    </Box>
                  </Group>
                )}
              </Group>

              {(recipe.dietaryRestrictions?.length || recipe.mealPrepFriendly) && (
                <Group gap="xs" wrap="wrap">
                  {recipe.mealPrepFriendly && (
                    <Badge size="md" variant="light" color="green">meal prep</Badge>
                  )}
                  {recipe.dietaryRestrictions?.map((r) => (
                    <Badge key={r} size="md" variant="light" color="green">{r}</Badge>
                  ))}
                </Group>
              )}

              {recipe.tags && recipe.tags.length > 0 && (
                <Group gap="xs" wrap="wrap">
                  {recipe.tags.map((t) => (
                    <Badge key={t} size="md" variant="outline" color="gray">{t}</Badge>
                  ))}
                </Group>
              )}

              {recipe.nutrition?.perServing && (
                <Group gap="xs" wrap="wrap">
                  <Box
                    component="span"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setNutritionOpen(true)}
                  >
                    <Group gap={4}>
                      <IconChartBar size={16} color="var(--mantine-color-blue-6)" />
                      <Text size="sm" c="blue.6">Nutrition facts</Text>
                    </Group>
                  </Box>
                  <Badge size="sm" variant="light" leftSection={<IconFlame size={12} />}>
                    {recipe.nutrition.perServing.calories} cal
                  </Badge>
                  <Badge size="sm" variant="light">
                    {Math.round(recipe.nutrition.perServing.protein)}g protein
                  </Badge>
                  <Badge size="sm" variant="light">
                    {Math.round(recipe.nutrition.perServing.carbohydrates)}g carbs
                  </Badge>
                  <Badge size="sm" variant="light">
                    {Math.round(recipe.nutrition.perServing.fat)}g fat
                  </Badge>
                </Group>
              )}
            </Stack>
          </Flex>

          <Grid>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <Stack gap="xs">
                <Title order={5}>Ingredients</Title>
                <Paper p="md" radius="lg" withBorder>
                  <Stack gap={0}>
                    {recipe.ingredients?.map((ing, i) => (
                      <Box key={i} py="xs" style={{
                        borderBottom: i < (recipe.ingredients?.length || 0) - 1
                          ? '1px solid var(--mantine-color-gray-2)' : 'none',
                      }}>
                        <Text size="sm">
                          <Text component="span" fw={500}>
                            {formatQuantity(ing.quantity, ing.unit)}
                          </Text>
                          {' '}{ing.name}
                          {ing.preparation && (
                            <Text component="span" c="dimmed"> — {ing.preparation}</Text>
                          )}
                        </Text>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Stack>

              {recipe.notes && (
                <Box mt="md" visibleFrom="md">
                  <Stack gap="xs">
                    <Title order={5}>Notes</Title>
                    <Paper p="md" radius="lg" withBorder>
                      <Text size="sm" c="dimmed" fs="italic">{recipe.notes}</Text>
                    </Paper>
                  </Stack>
                </Box>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <Stack gap="xs">
                <Title order={5}>Instructions</Title>
                <Stack gap="sm">
                  {recipe.instructions?.map((inst, i) => (
                    <Paper key={i} p="md" radius="lg" withBorder>
                      <Group gap="md" align="flex-start">
                        <Badge size="xl" variant="filled" color="blue" circle>
                          {i + 1}
                        </Badge>
                        <Text size="sm" style={{ flex: 1 }}>{inst.text}</Text>
                      </Group>
                    </Paper>
                  ))}
                </Stack>
              </Stack>
              {recipe.notes && (
                <Box mt="md" hiddenFrom="md">
                  <Stack gap="xs">
                    <Title order={5}>Notes</Title>
                    <Paper p="md" radius="lg" withBorder>
                      <Text size="sm" c="dimmed" fs="italic">{recipe.notes}</Text>
                    </Paper>
                  </Stack>
                </Box>
              )}
            </Grid.Col>
          </Grid>
      </Stack>

      {recipe.nutrition && (
        <NutritionDrawer
          opened={nutritionOpen}
          onClose={() => setNutritionOpen(false)}
          nutrition={recipe.nutrition}
          recipeName={recipe.name}
          recipeId={recipe.id}
          isOwner={false}
          ingredientCount={recipe.ingredients?.length}
        />
      )}
    </Modal>
  );
};

export const SeedQueuePage = () => {
  usePageTitle('Seed Queue | Meals You Love');
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<SeedRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [seedingLoading, setSeedingLoading] = useState(false);
  const [rejectAllOpen, setRejectAllOpen] = useState(false);
  const [rejectAllLoading, setRejectAllLoading] = useState(false);
  const [promptCount, setPromptCount] = useState<number | ''>(6);
  // Which slot a run seeds. Dinner is the catalog's historical default;
  // breakfast/lunch runs vary on format instead of cuisine.
  const [seedCategory, setSeedCategory] = useState<string>('dinner');
  // Review flow: id (not index) of the recipe open in the review modal, so
  // verdicts removing items from the list can't shift what's on screen.
  const [reviewId, setReviewId] = useState<string | null>(null);
  const [reviewActionLoading, setReviewActionLoading] = useState<'approve' | 'reject' | null>(null);

  const PAGE_SIZE = 100;

  const loadRecipes = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await fetchSeedQueue(user, PAGE_SIZE);
      setRecipes(data);
      setHasMore(data.length === PAGE_SIZE);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to load seed queue',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  // Append the next page so the review modal can run through the whole queue
  // without closing. Cursor = last loaded doc id.
  const loadMore = useCallback(async () => {
    if (!user || loadingMore || recipes.length === 0) return;
    setLoadingMore(true);
    try {
      const more = await fetchSeedQueue(user, PAGE_SIZE, recipes[recipes.length - 1].id);
      setHasMore(more.length === PAGE_SIZE);
      if (more.length > 0) {
        setRecipes((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...more.filter((r) => !seen.has(r.id))];
        });
      }
    } catch {
      // Non-fatal: the review flow just ends at the loaded boundary.
    } finally {
      setLoadingMore(false);
    }
  }, [user, loadingMore, recipes]);

  const reviewIndex = reviewId ? recipes.findIndex((r) => r.id === reviewId) : -1;
  const reviewRecipe = reviewIndex >= 0 ? recipes[reviewIndex] : null;

  // Prefetch the next page as the reviewer nears the end of the loaded list.
  useEffect(() => {
    if (reviewIndex >= 0 && hasMore && !loadingMore && reviewIndex >= recipes.length - 10) {
      loadMore();
    }
  }, [reviewIndex, hasMore, loadingMore, recipes.length, loadMore]);

  const isLoading = userLoading || loading;

  if (!userLoading && !user?.isAdmin) {
    return (
      <Container size="md" py="xl">
        <Alert icon={<IconAlertCircle size="1rem" />} title="Access Denied" color="red" radius="lg">
          You do not have admin privileges.
        </Alert>
      </Container>
    );
  }

  const handleApprove = async (id: string): Promise<boolean> => {
    if (!user) return false;
    try {
      await approveSeedRecipe(user, id);
      notifications.show({ title: 'Approved', message: 'Recipe added to catalog', color: 'green' });
      setRecipes((prev) => prev.filter((r) => r.id !== id));
      return true;
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
      return false;
    }
  };

  const handleDelete = async (id: string): Promise<boolean> => {
    if (!user) return false;
    try {
      await deleteSeedRecipe(user, id);
      notifications.show({ title: 'Deleted', message: 'Recipe removed', color: 'gray' });
      setRecipes((prev) => prev.filter((r) => r.id !== id));
      return true;
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
      return false;
    }
  };

  // Optimistic: the badge flips immediately, and a failed save flips it
  // back with the error. Recategorizing decides which slot's rotation can
  // find the recipe once approved.
  const handleRecategorize = async (id: string, category: string) => {
    if (!user) return;
    const previousRecipe = recipes.find((r) => r.id === id);
    // The server re-derives slots on a category change, so mirror that
    setRecipes((prev) =>
      prev.map((r) =>
        r.id === id ? { ...r, category, slots: deriveSlots(category) } : r
      )
    );
    try {
      await editSeedRecipe(user, id, { category });
    } catch (err: any) {
      setRecipes((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, category: previousRecipe?.category, slots: previousRecipe?.slots }
            : r
        )
      );
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleEditSlots = async (id: string, slots: SeedSlots) => {
    if (!user) return;
    const previous = recipes.find((r) => r.id === id)?.slots;
    setRecipes((prev) => prev.map((r) => (r.id === id ? { ...r, slots } : r)));
    try {
      await editSeedRecipe(user, id, { slots });
    } catch (err: any) {
      setRecipes((prev) =>
        prev.map((r) => (r.id === id ? { ...r, slots: previous } : r))
      );
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  // Verdict from the review modal: on success, advance to the next recipe in
  // the queue (the modal stays open), closing only when nothing is left.
  const handleReviewVerdict = async (action: 'approve' | 'reject') => {
    if (!reviewRecipe || reviewActionLoading) return;
    const nextId = recipes[reviewIndex + 1]?.id ?? recipes[reviewIndex - 1]?.id ?? null;
    setReviewActionLoading(action);
    try {
      const ok = action === 'approve'
        ? await handleApprove(reviewRecipe.id)
        : await handleDelete(reviewRecipe.id);
      if (ok) setReviewId(nextId);
    } finally {
      setReviewActionLoading(null);
    }
  };

  const handleGenerateImage = async (id: string) => {
    if (!user) return;
    try {
      const imageUrl = await generateSeedRecipeImage(user, id);
      setRecipes((prev) =>
        prev.map((r) => (r.id === id ? { ...r, imageUrl } : r))
      );
      notifications.show({ title: 'Image generated', message: 'Recipe image created', color: 'green' });
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    }
  };

  const handleRejectAll = async () => {
    if (!user) return;
    setRejectAllLoading(true);
    try {
      const deleted = await deleteAllSeedRecipes(user);
      notifications.show({ title: 'Rejected', message: `${deleted} recipes removed`, color: 'gray' });
      setRecipes([]);
      setRejectAllOpen(false);
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setRejectAllLoading(false);
    }
  };

  const handleTriggerSeeding = async () => {
    if (!user) return;
    setSeedingLoading(true);
    try {
      await triggerCatalogSeeding(
        user,
        promptCount || undefined,
        seedCategory === 'breakfast' || seedCategory === 'lunch'
          ? seedCategory
          : undefined
      );
      notifications.show({
        title: 'Seeding started',
        message: 'A new seeding run has been enqueued. Recipes will appear shortly.',
        color: 'blue',
      });
    } catch (err: any) {
      notifications.show({ title: 'Error', message: err.message, color: 'red' });
    } finally {
      setSeedingLoading(false);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Group justify="space-between" align="flex-start">
            <Group wrap="nowrap">
              <ActionIcon variant="subtle" size="lg" onClick={() => navigate(-1)}>
                <IconChevronLeft size={20} />
              </ActionIcon>
              <Stack gap={0}>
                <Title order={2}>Seed Queue</Title>
                <Text c="dimmed" size="sm">Review AI-generated recipes before adding to the catalog</Text>
              </Stack>
            </Group>
            <Group gap="xs">
              <Select
                data={[
                  { value: 'dinner', label: 'Dinners' },
                  { value: 'breakfast', label: 'Breakfasts' },
                  { value: 'lunch', label: 'Lunches' },
                ]}
                value={seedCategory}
                onChange={(v) => v && setSeedCategory(v)}
                w={130}
                size="sm"
                allowDeselect={false}
              />
              {/* Batches, not recipes: a bare number box read as a recipe
                  count and produced 5x the expected run */}
              <Tooltip
                label={`${promptCount || 6} batch${(promptCount || 6) === 1 ? '' : 'es'} of 5, about ${(promptCount || 6) * 5} recipes`}
                withArrow
              >
                <NumberInput
                  value={promptCount}
                  onChange={(v) => setPromptCount(v === '' ? '' : Number(v))}
                  min={1}
                  max={10}
                  w={70}
                  size="sm"
                  placeholder="6"
                />
              </Tooltip>
              <Button
                leftSection={<IconSeedling size="1rem" />}
                loading={seedingLoading}
                onClick={handleTriggerSeeding}
              >
                Seed
              </Button>
              <Button
                leftSection={<IconRefresh size="1rem" />}
                variant="outline"
                onClick={loadRecipes}
                visibleFrom="sm"
              >
                Refresh
              </Button>
              {recipes.length > 0 && (
                <Button
                  leftSection={<IconX size="1rem" />}
                  variant="outline"
                  color="red"
                  onClick={() => setRejectAllOpen(true)}
                >
                  Reject all
                </Button>
              )}
            </Group>
          </Group>
        </div>

        {isLoading ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {Array.from({ length: 6 }).map((_, i) => (
              <Card key={i} padding="lg" radius="lg" withBorder>
                <Card.Section>
                  <Skeleton height={200} radius={0} />
                </Card.Section>
                <Stack gap="xs" mt="md">
                  <Skeleton height={18} width="80%" />
                  <Skeleton height={14} width="95%" />
                  <Skeleton height={14} width="60%" />
                  <Group gap="md">
                    <Group gap="xs">
                      <Skeleton height={16} width={16} radius="sm" />
                      <Skeleton height={14} width={50} />
                    </Group>
                    <Group gap="xs">
                      <Skeleton height={16} width={16} radius="sm" />
                      <Skeleton height={14} width={20} />
                    </Group>
                    <Skeleton height={20} width={50} radius="xl" />
                  </Group>
                  <Group gap="xs">
                    <Skeleton height={20} width={70} radius="xl" />
                    <Skeleton height={20} width={55} radius="xl" />
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        ) : recipes.length === 0 ? (
          <Alert icon={<IconToolsKitchen2 size="1rem" />} title="No recipes" color="blue" radius="lg">
            No recipes in the queue.
          </Alert>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            {recipes.map((recipe) => (
              <SeedRecipeCard
                key={recipe.id}
                recipe={recipe}
                onOpenDetails={setReviewId}
                onApprove={handleApprove}
                onDelete={handleDelete}
                onGenerateImage={handleGenerateImage}
                onRecategorize={handleRecategorize}
                onEditSlots={handleEditSlots}
              />
            ))}
          </SimpleGrid>
        )}
      </Stack>

      {reviewRecipe && (
        <SeedRecipeReviewModal
          recipe={reviewRecipe}
          index={reviewIndex}
          total={recipes.length}
          hasMore={hasMore}
          actionLoading={reviewActionLoading}
          onClose={() => setReviewId(null)}
          onPrev={reviewIndex > 0 ? () => setReviewId(recipes[reviewIndex - 1].id) : null}
          onNext={reviewIndex < recipes.length - 1 ? () => setReviewId(recipes[reviewIndex + 1].id) : null}
          onApprove={() => handleReviewVerdict('approve')}
          onReject={() => handleReviewVerdict('reject')}
          onGenerateImage={handleGenerateImage}
          onRecategorize={handleRecategorize}
          onEditSlots={handleEditSlots}
        />
      )}

      <Modal
        opened={rejectAllOpen}
        onClose={() => setRejectAllOpen(false)}
        title="Reject all"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            This will permanently delete all {recipes.length} recipes from the seed queue. This action cannot be undone.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setRejectAllOpen(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={rejectAllLoading}
              onClick={handleRejectAll}
            >
              Reject all
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
